import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { api, apiBaseUrl, Badge, Button, Card, fmtDate, ListPage, Modal, tokenStore, useAuth, useFetch, usePagedList } from '@greenrinse/ui';
import type { Partner } from './PartnersPage';
import type { Site } from './SitesPage';
import { FLAG_LABELS, RecordDetail, statusTone } from './RecordDetail';

interface RecordRow {
  id: string; beforeAtUtc: string; afterAtUtc: string | null; siteLocalDate: string; plateEntered: string; siteId: string; siteName: string; partnerId: string | null; partnerName: string | null;
  washerName: string; vehicleType: string; service: string; status: string; flags: number; flagNames: string[]; isUnlisted: boolean; isFuzzyMatch: boolean; photoCount: number;
  beforeThumbUrl: string | null; afterThumbUrl: string | null;
}
interface Summary { total: number; byFlag: Record<string, number>; bySite: Record<string, number>; olderThan24h: number }

const STATUSES = ['Pending', 'Verified', 'Flagged', 'Approved', 'Rejected'];

/** Subscribes to the ops hub; calls `onChange` when the server broadcasts exception changes. */
export function useOpsHub(onChange: () => void) {
  useEffect(() => {
    const token = tokenStore.get()?.accessToken;
    if (!token) return;
    const conn = new HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/hubs/ops`, { accessTokenFactory: () => tokenStore.get()?.accessToken ?? '' })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
    let timer: number | undefined;
    const debounced = () => { window.clearTimeout(timer); timer = window.setTimeout(onChange, 800); };
    conn.on('exceptionsChanged', debounced);
    conn.on('recordVerified', debounced);
    conn.start().catch(() => { /* fall back to manual refresh */ });
    return () => { window.clearTimeout(timer); void conn.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default function RecordsPage({ exceptionsOnly = false }: { exceptionsOnly?: boolean }) {
  const { user } = useAuth();
  const canReview = ['SuperAdmin', 'RegionalManager', 'OperationsSupervisor'].includes(user?.role ?? '');
  const [status, setStatus] = useState('');
  const [siteId, setSiteId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [flag, setFlag] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const sites = useFetch<{ items: Site[] }>('/api/sites?pageSize=200');
  const partners = useFetch<{ items: Partner[] }>('/api/partners?pageSize=200');
  const list = usePagedList<RecordRow>('/api/records', {
    exceptionsOnly, status: exceptionsOnly ? undefined : status || undefined, siteId: siteId || undefined, partnerId: partnerId || undefined,
    hasFlag: flag || undefined, from: from || undefined, to: to || undefined, sortBy: exceptionsOnly ? 'oldest' : undefined,
  }, 50);
  const summary = useFetch<Summary>(exceptionsOnly ? '/api/records/exceptions/summary' : null);
  const [params] = useSearchParams();
  const [open, setOpen] = useState<string | null>(params.get('open'));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  useOpsHub(() => { list.reload(); summary.reload(); });

  const bulk = async (kind: 'approve' | 'reject') => {
    if (selected.size === 0 || !bulkReason.trim()) return;
    setBulkBusy(true);
    try { await api(`/api/records/bulk/${kind}`, { method: 'POST', body: { recordIds: [...selected], reason: bulkReason } }); setSelected(new Set()); setBulkReason(''); list.reload(); summary.reload(); }
    finally { setBulkBusy(false); }
  };
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <>
      {exceptionsOnly && summary.data && (
        <div className="gr-grid" style={{ marginBottom: 16 }}>
          <Card small><div className="gr-muted" style={{ fontSize: 13 }}>Waiting for review</div><div className="gr-stat">{summary.data.total}</div>{summary.data.olderThan24h > 0 && <Badge tone="error">{summary.data.olderThan24h} older than 24 h</Badge>}</Card>
          <Card small>
            <div className="gr-muted" style={{ fontSize: 13 }}>By reason</div>
            <div className="gr-row" style={{ marginTop: 6 }}>{Object.entries(summary.data.byFlag).sort((a, b) => b[1] - a[1]).map(([k, v]) => <button key={k} className="gr-badge gr-badge--warning" style={{ cursor: 'pointer', border: 0 }} onClick={() => setFlag(flag === k ? '' : k)}>{FLAG_LABELS[k] ?? k} · {v}</button>)}</div>
          </Card>
          <Card small>
            <div className="gr-muted" style={{ fontSize: 13 }}>By site</div>
            <div style={{ fontSize: 14, marginTop: 6 }}>{Object.entries(summary.data.bySite).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, v]) => <div key={k}>{k} <span className="gr-muted">· {v}</span></div>)}</div>
          </Card>
        </div>
      )}
      <ListPage<RecordRow>
        title={exceptionsOnly ? 'Exceptions queue' : 'Wash records'} state={list} rowKey={(r) => r.id} onRowClick={(r) => setOpen(r.id)}
        filters={<>
          {!exceptionsOnly && <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>}
          <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={partnerId} onChange={(e) => setPartnerId(e.target.value)}><option value="">All partners</option>{(partners.data?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={siteId} onChange={(e) => setSiteId(e.target.value)}><option value="">All sites</option>{(sites.data?.items ?? []).filter((s) => !partnerId || s.partnerId === partnerId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
          <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={flag} onChange={(e) => setFlag(e.target.value)}><option value="">Any flag</option>{Object.entries(FLAG_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <input className="gr-input" type="date" style={{ width: 'auto', minHeight: 40 }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <input className="gr-input" type="date" style={{ width: 'auto', minHeight: 40 }} value={to} onChange={(e) => setTo(e.target.value)} />
        </>}
        actions={exceptionsOnly && canReview && selected.size > 0 ? (
          <div className="gr-row">
            <input className="gr-input" style={{ minHeight: 40, width: 220 }} placeholder={`Reason for ${selected.size} record(s)`} value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} />
            <Button loading={bulkBusy} disabled={!bulkReason.trim()} onClick={() => void bulk('approve')}>Approve {selected.size}</Button>
            <Button variant="danger" loading={bulkBusy} disabled={!bulkReason.trim()} onClick={() => void bulk('reject')}>Reject {selected.size}</Button>
          </div>
        ) : undefined}
        columns={[
          ...(exceptionsOnly && canReview ? [{ key: 'sel', header: '', width: 36, render: (r: RecordRow) => <input type="checkbox" checked={selected.has(r.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(r.id)} /> }] : []),
          { key: 'photos', header: '', width: 120, render: (r) => <div className="gr-row" style={{ gap: 4, flexWrap: 'nowrap' }}>{[r.beforeThumbUrl, r.afterThumbUrl].map((u, i) => u ? <img key={i} src={u} alt="" style={{ width: 52, height: 40, objectFit: 'cover', borderRadius: 8 }} /> : <div key={i} style={{ width: 52, height: 40, borderRadius: 8, background: 'var(--gr-surface-3)' }} />)}</div> },
          { key: 'plate', header: 'Plate', render: (r) => <><strong style={{ fontFamily: 'var(--gr-font-heading)', letterSpacing: '0.06em' }}>{r.plateEntered}</strong><div className="gr-muted" style={{ fontSize: 12 }}>{r.vehicleType} · {r.service}{r.isUnlisted ? ' · unlisted' : r.isFuzzyMatch ? ' · fuzzy' : ''}</div></> },
          { key: 'when', header: 'When', render: (r) => <>{fmtDate(r.beforeAtUtc)}<div className="gr-muted" style={{ fontSize: 12 }}>{r.siteLocalDate}</div></> },
          { key: 'site', header: 'Site', render: (r) => <>{r.siteName}<div className="gr-muted" style={{ fontSize: 12 }}>{r.partnerName}</div></> },
          { key: 'washer', header: 'Washer', render: (r) => r.washerName },
          { key: 'status', header: 'Status', render: (r) => <div className="gr-row" style={{ gap: 4 }}><Badge tone={statusTone(r.status)}>{r.status}</Badge>{r.flagNames.map((f) => <Badge key={f} tone="warning">{FLAG_LABELS[f] ?? f}</Badge>)}</div> },
        ]}
      />
      <Modal open={open !== null} wide title="Wash record" onClose={() => setOpen(null)}>
        {open && <RecordDetail id={open} onChanged={() => { list.reload(); summary.reload(); }} />}
      </Modal>
    </>
  );
}
